import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AppFeatureService, AppRoleService } from '@nk/access-block';
import {
  DATA_SOURCE_API_SERVICE_TOKEN,
  DataSourceApiService,
  SENSITIVE_DATA_API_SERVICE_TOKEN,
  SensitiveDataApiService,
} from '@nk/api-lib';
import {
  AppFeatureKey,
  AppRoleKey,
  DataSourceCategoryEntity,
  SensitiveDataCompanyAccessEntity,
  SensitiveDataTypeKey,
} from '@nk/data-entities-lib';
import {
  DataSourceCategoryDict,
  getDataSourceCategoryList,
  sortAndFilterDataSourceCategories,
} from '@nk/data-source-lib';
import { CheckboxComponent, SelectAllButtonComponent } from '@nk/ui-lib';
import { arrayAll, arrayToPickyDict, isEqualArrays } from '@nk/utils-lib';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { PaymentRemarksSectionComponent } from '../payment-remarks-section/payment-remarks-section.component';

@Component({
  selector: 'nk-data-source-categories-control',
  templateUrl: './data-source-categories-control.component.html',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TranslateModule,
    CheckboxComponent,
    SelectAllButtonComponent,
    PaymentRemarksSectionComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataSourceCategoriesControlComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataSourceCategoriesControlComponent implements ControlValueAccessor, OnInit {
  public companyUuid = input<string>();
  public watchlistUuid = input<string>();
  public hidePaymentRemarksSection = input<boolean>(false);

  public form = new FormGroup<Record<string, FormControl<boolean>>>({});
  public hasFeatureAndRoleAccess$!: Observable<boolean>;
  public sensitiveDataCompanyAccessEntity$!: Observable<SensitiveDataCompanyAccessEntity | undefined>;
  public isPaymentRemarksOn = signal<boolean | null>(null);
  public dataSourceCategoryList$!: Observable<DataSourceCategoryEntity[]>;

  private _sensitiveDataService = inject<SensitiveDataApiService>(SENSITIVE_DATA_API_SERVICE_TOKEN);
  private _dataSourceService = inject<DataSourceApiService>(DATA_SOURCE_API_SERVICE_TOKEN);
  private _appFeatureService = inject(AppFeatureService);
  private _appRoleService = inject(AppRoleService);
  private _destroyRef = inject(DestroyRef);

  private _disabled = false;
  private _value?: string[];

  public ngOnInit(): void {
    this.form.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((dataSourceCategoriesDict) => {
        this.setDataSourceCategories(dataSourceCategoriesDict as DataSourceCategoryDict);
      });

    const companyUuid = this.companyUuid();
    const watchlistUuid = this.watchlistUuid();

    if (companyUuid) {
      this.sensitiveDataCompanyAccessEntity$ = this._sensitiveDataService.loadTriggered$.pipe(
        switchMap(() =>
          this._sensitiveDataService.sensitiveDataCompanyAccessEntityListCache
            .get()
            .pipe(map((entities) => entities.find((i) => i.company_uuid === companyUuid))),
        ),
      );
    }
    this.hasFeatureAndRoleAccess$ = this._sensitiveDataService.hasFeatureAndRoleAccess();

    const claimValidSensitiveDataTypeKeys$ = ((): Observable<SensitiveDataTypeKey[]> => {
      if (companyUuid) {
        return this._sensitiveDataService.claimValidSensitiveDataTypeKeysByCompany(companyUuid, [
          SensitiveDataTypeKey.payment_remark,
        ]);
      }
      if (watchlistUuid) {
        return this._sensitiveDataService.claimValidSensitiveDataTypeKeysByWatchlist(watchlistUuid, [
          SensitiveDataTypeKey.payment_remark,
        ]);
      }
      throw new Error('neither companyUuid nor watchlistUuid were provided');
    })();
    this.dataSourceCategoryList$ = this._sensitiveDataService.loadTriggered$.pipe(
      switchMap(() =>
        combineLatest([
          this._dataSourceService.dataSourceCategoryListCache.get('event'),
          this._appFeatureService.claimValidAppFeatureKeys([
            AppFeatureKey.payment_remark,
            AppFeatureKey.company_risk_score,
          ]),
          this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]),
          claimValidSensitiveDataTypeKeys$,
        ]).pipe(
          map(([dataSourceCategories, validFeatures, validRoles, validSensitiveDataTypes]) => {
            const hasPaymentRemarksAccess = arrayAll([
              validFeatures.includes(AppFeatureKey.payment_remark),
              validRoles.includes(AppRoleKey.credit_check),
              validSensitiveDataTypes.includes(SensitiveDataTypeKey.payment_remark),
            ]);
            this.isPaymentRemarksOn.set(hasPaymentRemarksAccess);
            // The `/data-source-category` endpoint is an open catalog, so
            // categories whose events are actually feature-gated need to be
            // filtered out client-side -- otherwise we offer a checkbox that
            // returns zero events from the gated event-feed endpoints.
            const filteredCategories = dataSourceCategories.filter((dsc) => {
              if (!hasPaymentRemarksAccess && dsc.data_source_category_key === 'event^^^payment_remark') {
                return false;
              }
              if (
                !validFeatures.includes(AppFeatureKey.company_risk_score) &&
                dsc.data_source_category_key === 'event^^^company_event_risk_score'
              ) {
                return false;
              }
              return true;
            });
            return sortAndFilterDataSourceCategories(filteredCategories);
          }),
        ),
      ),
      tap((dataSourceCategories) => {
        for (const category of dataSourceCategories) {
          this.form.addControl(category.data_source_category_key, new FormControl(false, { nonNullable: true }), {
            emitEvent: false,
          });
        }
      }),
    );
  }

  public selectAllDataSourceCategories(state: boolean): void {
    const dataSourceCategoryKeys = Object.keys(this.form.controls);
    this.form.patchValue(
      arrayToPickyDict(
        dataSourceCategoryKeys,
        (key) => key,
        () => state,
      ),
    );
  }

  public isAllDataSourceCategoriesSelected(): boolean {
    const dataSourceCategoryDict = this.form.value;
    return (
      dataSourceCategoryDict &&
      Object.keys(dataSourceCategoryDict).length > 0 &&
      Object.values(dataSourceCategoryDict).every((i) => i === true)
    );
  }

  public setDataSourceCategories(dataSourceCategoriesDict: DataSourceCategoryDict): void {
    if (!this._disabled && dataSourceCategoriesDict) {
      this._value = getDataSourceCategoryList(dataSourceCategoriesDict);
      this.writeValue(this._value);
    }
  }

  public onChange = (value: string[]): void => {};

  public onTouched = (value: string[]): void => {};

  public get value(): string[] | undefined {
    return this._value;
  }

  public writeValue(value?: string[]): void {
    const dataSourceCategoryList = getDataSourceCategoryList(this.form.value as DataSourceCategoryDict);
    if (value) {
      if (!isEqualArrays(value, dataSourceCategoryList)) {
        const dataSourceCategoriesDict = Object.keys(this.form.value).reduce<DataSourceCategoryDict>((res, key) => {
          res[key] = value.includes(key);
          return res;
        }, {});
        this.form.setValue(dataSourceCategoriesDict);
      }
      this.onChange(value);
    }
  }

  public registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: (value: string[]) => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }
}
